/**
 * Server Error Context
 *
 * Global context for handling 5xx server errors across the application.
 * Shows a user-friendly dialog with chatbot support link.
 */

import { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { ServerErrorDialog } from "@/components/ServerErrorDialog";

interface ServerErrorContextType {
  showServerError: () => void;
  hideServerError: () => void;
  isServerErrorVisible: boolean;
}

const ServerErrorContext = createContext<ServerErrorContextType | undefined>(undefined);

// Singleton for use outside React components (e.g., in API interceptors)
let serverErrorHandler: (() => void) | null = null;

export const triggerServerError = () => {
  if (serverErrorHandler) {
    serverErrorHandler();
  }
};

interface ServerErrorProviderProps {
  children: ReactNode;
}

export const ServerErrorProvider = ({ children }: ServerErrorProviderProps) => {
  const [isServerErrorVisible, setIsServerErrorVisible] = useState(false);

  const showServerError = useCallback(() => {
    setIsServerErrorVisible(true);
  }, []);

  const hideServerError = useCallback(() => {
    setIsServerErrorVisible(false);
  }, []);

  // Register the handler for use outside React
  serverErrorHandler = showServerError;

  return (
    <ServerErrorContext.Provider
      value={{
        showServerError,
        hideServerError,
        isServerErrorVisible,
      }}
    >
      {children}
      <ServerErrorDialog
        open={isServerErrorVisible}
        onClose={hideServerError}
      />
    </ServerErrorContext.Provider>
  );
};

export const useServerError = (): ServerErrorContextType => {
  const context = useContext(ServerErrorContext);
  if (context === undefined) {
    throw new Error("useServerError must be used within a ServerErrorProvider");
  }
  return context;
};
